<script setup lang="ts">
import { CheckIcon, ExclamationCircleIcon, QuestionMarkCircleIcon } from "@heroicons/vue/20/solid";
import { twMerge } from "tailwind-merge";
import { computed } from "vue";

import { useDotations } from "@/composables/api/useDotations";
import { useUser } from "@/composables/api/useUser";
import type { Reward } from "@/rewards";

import LockClosedIcon from "@/components/icons/LockClosedIcon.vue";
import LockOpenedIcon from "@/components/icons/LockOpenedIcon.vue";
import Dialog from "@/components/ui/Dialog.vue";

const props = defineProps<{ reward: Reward }>();

const { data: user } = useUser();
const { data: dotations } = useDotations();

const backendDotation = computed(() => dotations.value?.find((d) => d.slug === props.reward.slug));
const isLocked = computed(() => user.value && user.value.loyalty_points < props.reward.points);
const isAlreadyWon = computed(() =>
  user.value?.won_dotation_ids.includes(backendDotation.value?.id ?? "unknown"),
);
</script>

<template>
  <article
    class="relative flex w-[270px] flex-col rounded-[20px] bg-white shadow-xl"
    :aria-label="`Cadeau : ${$t(reward.name)}`"
  >
    <span
      role="status"
      :class="
        twMerge(
          'absolute top-4 left-1/2 z-[1] flex w-full max-w-44 -translate-x-1/2 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-center text-xs leading-[110%] tracking-tight shadow-xl lg:max-w-56 lg:gap-2.5 lg:px-4 lg:py-2 lg:text-sm [&>svg]:size-5 [&>svg]:shrink-0 lg:[&>svg]:size-6',
          isLocked && user?.current_mechanic !== 'lactel' && 'bg-galbani text-white',
          isLocked && user?.current_mechanic === 'lactel' && 'bg-lactel-blue text-white',
          isAlreadyWon && user?.current_mechanic !== 'lactel' && 'bg-topchef text-galbani',
          isAlreadyWon && user?.current_mechanic === 'lactel' && 'bg-lactel-green-600 text-white',
          !isLocked &&
            !isAlreadyWon &&
            user?.current_mechanic !== 'lactel' &&
            'text-galbani bg-white',
          !isLocked &&
            !isAlreadyWon &&
            user?.current_mechanic === 'lactel' &&
            'text-lactel-blue bg-[#FDE456]',
        )
      "
    >
      <template v-if="isLocked">
        {{ $t("gift.locked", { n: reward.points }) }}
      </template>

      <template v-else-if="isAlreadyWon">
        {{ $t("gift.alreadyWon") }}
        <CheckIcon aria-hidden="true" />
      </template>

      <template v-else-if="!isLocked && !isAlreadyWon">
        {{ $t("gift.available") }}
        <LockOpenedIcon aria-hidden="true" />
      </template>
    </span>

    <div class="relative flex h-40 items-center justify-center overflow-hidden">
      <img
        :src="reward.images.carousel"
        :alt="$t(reward.name)"
        :class="
          twMerge(
            'block h-26 w-full rounded-t-[20px] object-contain object-center lg:h-24',
            isLocked && user?.current_mechanic !== 'lactel' && 'grayscale',
            reward.i18nName === 'bag' && 'h-36 lg:h-32',
            reward.i18nName === 'socks' &&
              'absolute top-0 -right-2 h-44 w-auto object-contain object-right-top lg:h-48',
            reward.i18nName === 'magnets' && 'h-36 lg:h-32',
          )
        "
      />
    </div>

    <span
      v-if="user?.current_mechanic === 'lactel'"
      class="font-montserrat relative z-[1] w-full pt-3 pb-1 text-center text-[12.5px] leading-[110%] font-normal text-[#001065]"
      v-html="$t('gift.image.mention')"
    />

    <div
      :class="
        twMerge(
          'flex w-full flex-1 flex-col items-center justify-center gap-2 rounded-b-[20px] px-1 py-3',
          isLocked && user?.current_mechanic === 'galbani' && 'bg-[#5C5C5C]',
          isLocked &&
            user?.current_mechanic === 'lactel' &&
            'bg-lactel-blue-400 justify-between lg:min-h-[136.97px]',
          !isLocked && user?.current_mechanic === 'galbani' && 'bg-galbani',
          !isLocked &&
            user?.current_mechanic === 'lactel' &&
            'bg-lactel-blue-400 justify-between lg:min-h-[136.97px]',
          user?.current_mechanic === 'lactel' && 'curved-edge',
        )
      "
    >
      <p
        :class="
          twMerge(
            'font-brygada flex h-[70px] items-center justify-center text-center text-[16px] leading-tight font-bold text-white lg:text-[28px]',
            user?.current_mechanic === 'lactel' &&
              'font-mitr h-full pt-[25px] leading-[18px] font-medium lg:text-[23px] lg:leading-[26px]',
            reward.i18nName === 'bag' && 'lg:h-[75px]',
          )
        "
        v-html="$t(reward.name)"
      />

      <Dialog>
        <template #trigger>
          <button
            type="button"
            class="flex cursor-pointer gap-2 text-center text-xs text-white/80 underline lg:text-base lg:tracking-tight"
            :aria-label="`Voir les informations sur ${$t(reward.name)}`"
          >
            <QuestionMarkCircleIcon
              v-if="user?.current_mechanic === 'galbani'"
              class="size-4 lg:size-7"
              aria-hidden="true"
            />

            <ExclamationCircleIcon v-else class="size-4 rotate-180 lg:size-7" aria-hidden="true" />

            <span>{{ $t("gift.details") }}</span>
          </button>
        </template>

        <template #content>
          <div class="flex w-full flex-col gap-5">
            <div class="flex flex-col gap-1.5">
              <h2
                :class="
                  twMerge(
                    'text-galbani text-2xl leading-tight font-semibold',
                    user?.current_mechanic === 'lactel' && 'text-lactel-blue',
                  )
                "
              >
                {{ $t(`dialog.rewards.${reward.i18nName}.title`) }}
              </h2>

              <p class="text-secondary text-lg font-medium italic">
                {{ $t(`dialog.rewards.${reward.i18nName}.requiredPoints`) }}
              </p>
            </div>

            <div
              class="text-primary text-base"
              v-html="$t(`dialog.rewards.${reward.i18nName}.description`)"
            />
          </div>
        </template>
      </Dialog>
    </div>

    <span
      v-if="isLocked"
      :class="
        twMerge(
          'bg-galbani absolute top-20 left-1/2 flex size-11 -translate-x-1/2 items-center justify-center rounded-full',
          user?.current_mechanic === 'lactel' && 'bg-lactel-blue top-30',
        )
      "
      aria-label="Récompense verrouillée"
    >
      <LockClosedIcon class="size-5 text-white" aria-hidden="true" />
    </span>
  </article>
</template>

<style scoped lang="css">
.curved-edge {
  --c: 30px;
  mask: radial-gradient(55% var(--c) at top, #0000 calc(100% - 1px), #000);
}
</style>
